import React, { useMemo } from 'react';
import { Row, Col } from 'antd';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';

import {
  statesSelector,
  citiesSelector,
  isStatesLoadingSelector,
  isCitiesLoadingSelector,
  isCountryCitiesLoadingSelector,
  countryCitiesSelector,
  countriesSelector,
  isCountriesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import FormItem from 'components/formik/FormItem';
import { SplitInfoFormValues } from '../SplitInfo';
import InputNumber from 'components/formik/InputNumber';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import { SplitTypeEnum } from 'api/backofficeApiClient';
import { checkIsCountriesWithStatesDropdown } from 'utils/validators/countryUtils';

type Props = {
  formNamePrefix: 'existingPartnerData' | 'newPartnerData';
  formik?: FormikProps<SplitInfoFormValues>;
  disabled?: boolean;
};

export default function PartnerSection(props: Props) {
  const { formNamePrefix, formik, disabled = false } = props;
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const isCountryCitiesLoading = useAppSelector(isCountryCitiesLoadingSelector);
  const countryCities = useAppSelector(countryCitiesSelector);
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);

  const filteredStates = useMemo(
    () =>
      states.filter(
        (state) => state.countryId === formik?.values[formNamePrefix]?.country,
      ),
    // eslint-disable-next-line
    [formik?.values[formNamePrefix]?.country, states, formNamePrefix],
  );

  const filteredCities = useMemo(
    () =>
      cities.filter(
        (city) => city.stateId === formik?.values[formNamePrefix]?.state,
      ),
    // eslint-disable-next-line
    [formik?.values[formNamePrefix]?.state, cities, formNamePrefix],
  );

  const filteredInternationalCities = useMemo(
    () =>
      countryCities.filter(
        (city) => city.countryId === formik?.values[formNamePrefix]?.country,
      ),
    // eslint-disable-next-line
    [formik?.values[formNamePrefix]?.country, countryCities, formNamePrefix],
  );

  const isInternationalCountry =
    formik?.values.splitType === SplitTypeEnum.ExistingPartner
      ? !checkIsCountriesWithStatesDropdown(formik?.values.existingPartnerData?.country)
      : !checkIsCountriesWithStatesDropdown(formik?.values[formNamePrefix]?.country);

  const handleStateChange = () => {
    const selectedCountry = formik?.values[formNamePrefix]?.country;

    if (checkIsCountriesWithStatesDropdown(selectedCountry)) {
      formik?.setFieldValue(`${formNamePrefix}.city`, '', false);
    } else {
      formik?.setFieldValue(
        `${formNamePrefix}.internationalCityName`,
        '',
        false,
      );
    }
  };

  const handleCountryChange = (
    countryId: number,
    formik?: FormikProps<SplitInfoFormValues>,
  ) => {
    formik?.setFieldValue(`${formNamePrefix}.city`, '', false);
    formik?.setFieldValue(`${formNamePrefix}.state`, undefined, false);
    formik?.setFieldValue(`${formNamePrefix}.internationalCityName`, '', false);
    formik?.setFieldValue(
      `${formNamePrefix}.internationalStateName`,
      '',
      false,
    );
  };

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <FormItem
            label="Subcontractor rate"
            name={`${formNamePrefix}.subcontractorRate`}
          >
            <InputNumber
              name={`${formNamePrefix}.subcontractorRate`}
              placeholder="Enter subcontractor rate"
              className="full-width"
              precision={2}
              disabled={disabled}
            />
          </FormItem>
        </Col>
      </Row>

      <Row gutter={40}>
        <Col xs={24} md={6}>
          <FormItem
            name={`${formNamePrefix}.country`}
            label="Country *"
            data-id="newSplitCountryField"
          >
            <FormikSelectWithLoading
              isLoading={isCountriesLoading}
              name={`${formNamePrefix}.country`}
              disabled={disabled}
              showSearch
              placeholder="Select country"
              optionFilterProp="children"
              onChange={(value: number) => handleCountryChange(value, formik)}
            >
              {countries.map((country) => (
                <FormikSelectWithLoading.Option
                  key={`select-country-${country.id}`}
                  value={country.id || -1}
                >
                  {country.name}
                </FormikSelectWithLoading.Option>
              ))}
            </FormikSelectWithLoading>
          </FormItem>
        </Col>

        {!isInternationalCountry && (
          <Col xs={24} md={6}>
            <FormItem name={`${formNamePrefix}.state`} label="State *">
              <FormikSelectWithLoading
                isLoading={isStatesLoading}
                name={`${formNamePrefix}.state`}
                showSearch
                placeholder="Select state"
                optionFilterProp="children"
                disabled={disabled || !formik?.values[formNamePrefix]?.country}
                onChange={handleStateChange}
              >
                {filteredStates.map((state) => (
                  <FormikSelectWithLoading.Option
                    key={`select-state-${state.id}`}
                    value={state.id || -1}
                  >
                    {state.name}
                  </FormikSelectWithLoading.Option>
                ))}
              </FormikSelectWithLoading>
            </FormItem>
          </Col>
        )}

        {isInternationalCountry && (
          <Col xs={24} md={6}>
            <FormItem
              name={`${formNamePrefix}.internationalStateName`}
              label="State *"
            >
              <Input
                name={`${formNamePrefix}.internationalStateName`}
                placeholder="Enter state"
                disabled={disabled || !formik?.values[formNamePrefix]?.country}
                autoComplete="off"
              />
            </FormItem>
          </Col>
        )}

        {!isInternationalCountry && (
          <Col xs={24} md={6}>
            <FormItem name={`${formNamePrefix}.city`} label="City *">
              <FormikAutoCompleteWithLoading
                isLoading={isCitiesLoading}
                name={`${formNamePrefix}.city`}
                filterOption
                placeholder="Enter city"
                showSearch
                disabled={disabled || !formik?.values[formNamePrefix]?.state}
              >
                {filteredCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`city-state-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        )}

        {isInternationalCountry && (
          <Col xs={24} md={6}>
            <FormItem
              name={`${formNamePrefix}.internationalCityName`}
              label="City *"
            >
              <FormikAutoCompleteWithLoading
                isLoading={isCountryCitiesLoading}
                name={`${formNamePrefix}.internationalCityName`}
                filterOption
                disabled={
                  disabled ||
                  !formik?.values[formNamePrefix]?.internationalStateName
                }
                placeholder="Enter city"
                showSearch
              >
                {filteredInternationalCities.map((city) => (
                  <FormikAutoCompleteWithLoading.Option
                    key={`city-state-${city.id}`}
                    value={city.name || ''}
                  >
                    {city.name}
                  </FormikAutoCompleteWithLoading.Option>
                ))}
              </FormikAutoCompleteWithLoading>
            </FormItem>
          </Col>
        )}

        <Col xs={24} md={6}>
          <FormItem label="Address *" name={`${formNamePrefix}.address`}>
            <Input
              name={`${formNamePrefix}.address`}
              placeholder="Enter address"
              disabled={disabled}
              autoComplete="off"
            />
          </FormItem>
        </Col>

        <Col xs={24} md={6}>
          <FormItem label="ZIP" name={`${formNamePrefix}.zip`}>
            <Input
              name={`${formNamePrefix}.zip`}
              className="full-width"
              placeholder="Enter ZIP code"
              disabled={disabled}
              autoComplete="off"
            />
          </FormItem>
        </Col>
      </Row>
    </>
  );
}
